import { BowelMovement } from '../types';

export type HealthStatus = 'good' | 'warning' | 'alert';

export interface HealthSummary {
  status: HealthStatus;
  title: string;
  reasons: string[];
  recommendations: string;
}

/** Map app BowelMovement to normalized format for algorithm */
const toNormalizedMovement = (m: BowelMovement) => ({
  date: m.date,
  consistency: m.consistency,
  blood: m.hasBlood ? (m.bloodAmount ?? 1) : 0,
  mucus: m.hasMucus ? (m.mucusAmount ?? 1) : 0,
  pain: m.pain,
  urgency: m.urgency,
  gas: m.hasGas ? (m.gasIntensity ?? 1) : 0,
});

/** Convert consistency string to Bristol number (1-7) */
const getBristolNumber = (consistency: string): number => {
  switch (consistency) {
    case 'twarda': return 1;
    case 'normalna': return 4;
    case 'miękka': return 6;
    case 'płynna': return 7;
    default: return 4;
  }
};

const getTodayBowelMovements = (movements: BowelMovement[]): BowelMovement[] => {
  const today = new Date().toISOString().split('T')[0];
  return movements.filter((m) => m.date === today);
};

/** Sort by date descending, most recent first */
const sortByDateDesc = (a: BowelMovement, b: BowelMovement) =>
  new Date(b.date).getTime() - new Date(a.date).getTime();

/**
 * Calculate health status based on recent bowel movements.
 * Algorithm inspired by Mayo Score and IBD guidelines.
 */
export const calculateHealthStatus = (
  recentMovements: BowelMovement[]
): HealthSummary => {
  if (recentMovements.length === 0) {
    return {
      status: 'good',
      title: 'Brak danych',
      reasons: ['Dodaj pierwsze wypróżnienie aby zobaczyć analizę'],
      recommendations: 'Regularnie śledź swoje objawy',
    };
  }

  const sorted = [...recentMovements].sort(sortByDateDesc);
  const todayMovements = getTodayBowelMovements(sorted);
  const todayCount = todayMovements.length;

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const last7Days = sorted.filter((m) => new Date(m.date) >= sevenDaysAgo);
  const dataForAnalysis = last7Days.length > 0 ? last7Days : sorted;
  const normalized = dataForAnalysis.map(toNormalizedMovement);

  const avgBlood =
    normalized.reduce((sum, m) => sum + m.blood, 0) / normalized.length;
  const maxBlood = Math.max(...normalized.map((m) => m.blood));
  const avgPain =
    normalized.reduce((sum, m) => sum + m.pain, 0) / normalized.length;
  const avgUrgency =
    normalized.reduce((sum, m) => sum + m.urgency, 0) / normalized.length;
  const avgMucus =
    normalized.reduce((sum, m) => sum + m.mucus, 0) / normalized.length;

  const consistencies = dataForAnalysis.map((m) => getBristolNumber(m.consistency));
  const hasType7 = consistencies.some((c) => c === 7);
  const hasType6 = consistencies.some((c) => c === 6);
  const mostlyNormal =
    consistencies.filter((c) => c >= 3 && c <= 5).length >
    consistencies.length / 2;

  const reasons: string[] = [];

  // ═══════════════════════════════════════════════════════
  // 🔴 CZERWONY - Zaostrzenie (ALERT)
  // ═══════════════════════════════════════════════════════

  if (maxBlood >= 3) {
    reasons.push(`Znacząca krew w stolcu (${maxBlood}/5)`);
  }

  if (todayCount > 6) {
    reasons.push(`${todayCount} wypróżnień dzisiaj (>6 = ciężka aktywność)`);
  }

  if (hasType7 && maxBlood > 0) {
    reasons.push('Biegunka wodna z krwią');
  }

  if (avgPain >= 4 && maxBlood > 0) {
    reasons.push(`Silny ból (${avgPain.toFixed(1)}/5) z krwawieniem`);
  }

  if (reasons.length > 0) {
    return {
      status: 'alert',
      title: 'Zły dzień - Możliwe zaostrzenie',
      reasons,
      recommendations: '⚠️ Rozważ kontakt z lekarzem. Objawy wymagają uwagi.',
    };
  }

  // ═══════════════════════════════════════════════════════
  // 🟡 ŻÓŁTY - Łagodne objawy (WARNING)
  // ═══════════════════════════════════════════════════════

  if (avgBlood >= 1 && avgBlood < 3) {
    reasons.push(`Smugi krwi w stolcu (${avgBlood.toFixed(1)}/5)`);
  }

  if (todayCount >= 4 && todayCount <= 6) {
    reasons.push(`${todayCount} wypróżnień dzisiaj (zwiększona częstość)`);
  }

  if (hasType6) {
    reasons.push('Luźna konsystencja');
  }

  if (avgUrgency >= 4) {
    reasons.push(`Nagła potrzeba defekacji (${avgUrgency.toFixed(1)}/5)`);
  }

  if (avgPain >= 2 && avgPain < 4) {
    reasons.push(`Umiarkowany ból (${avgPain.toFixed(1)}/5)`);
  }

  if (avgMucus >= 3) {
    reasons.push(`Zwiększony śluz (${avgMucus.toFixed(1)}/5)`);
  }

  if (reasons.length > 0) {
    return {
      status: 'warning',
      title: 'Średni dzień - Wymagana obserwacja',
      reasons,
      recommendations: '💡 Monitoruj objawy przez kolejne dni',
    };
  }

  // ═══════════════════════════════════════════════════════
  // 🟢 ZIELONY - Remisja (GOOD)
  // ═══════════════════════════════════════════════════════

  reasons.push('✓ Brak krwi w stolcu');

  if (todayCount >= 1 && todayCount <= 3) {
    reasons.push(`✓ ${todayCount} wypróżnień dzisiaj (normalna częstość)`);
  }

  if (mostlyNormal) {
    reasons.push('✓ Konsystencja prawidłowa');
  }

  if (avgPain <= 1) {
    reasons.push('✓ Minimalny dyskomfort');
  }

  if (avgUrgency <= 2) {
    reasons.push('✓ Kontrolowana potrzeba');
  }

  return {
    status: 'good',
    title: 'Dobry dzień - Remisja',
    reasons,
    recommendations: '👍 Kontynuuj obecne leczenie',
  };
};
